import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Hero Banner */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 text-white">
        <div className="mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <Link to="/register" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-6 transition">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Registration
          </Link>
          <div>
            <div className="inline-block mb-4 px-4 py-2 rounded-full bg-sky-500/20 border border-sky-400/30 backdrop-blur-sm">
              <p className="text-xs font-semibold uppercase tracking-widest text-sky-200">Your Data, Your Rights</p>
            </div>
            <h1 className="text-5xl font-extrabold tracking-tight mb-4">Privacy Policy</h1>
            <p className="text-lg text-slate-300">
              We believe transparency builds trust. Learn how we collect, use, and protect your personal information.
            </p>
            <p className="mt-6 text-sm text-slate-400">Last updated: March 16, 2026</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-slate-600 mb-4">Sections</h3>
              <nav className="space-y-2.5">
                {[
                  { id: 'introduction', label: '1. Introduction' },
                  { id: 'information', label: '2. Information We Collect' },
                  { id: 'use', label: '3. How We Use Your Data' },
                  { id: 'sharing', label: '4. Data Sharing' },
                  { id: 'security', label: '5. Data Security' },
                  { id: 'retention', label: '6. Data Retention' },
                  { id: 'rights', label: '7. Your Rights' },
                  { id: 'cookies', label: '8. Cookies' },
                  { id: 'contact', label: '9. Contact Us' },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block text-sm text-slate-600 hover:text-sky-600 transition-colors text-left font-medium py-1"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-4 space-y-10">
            {/* Section 1 */}
            <section id="introduction" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">1. Introduction</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Welcome to VMC Reviews ("we", "our", or "us"). VMC Reviews ("Company") operates the VMC Reviews website and the VMC Reviews mobile application (the "Service").
                </p>
                <p>
                  This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data. We use your data to provide and improve the Service.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section id="information" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">2. Information We Collect</h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Personal Data Collected:</h3>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    When you register for an account, we collect:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span><strong>Contact Information:</strong> Email address, phone number, name</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span><strong>Profile Data:</strong> Location, city, country, profile picture</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span><strong>Account Credentials:</strong> Password (encrypted)</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Usage Data Collected:</h3>
                  <p className="text-slate-700 leading-relaxed mb-4">
                    We automatically collect certain information about your device and how you interact with our Service:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>IP address and device identifiers</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>Browser type, version, and operating system</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>Pages visited and time spent on pages</span>
                    </li>
                    <li className="text-slate-700 flex gap-3">
                      <span className="text-sky-600 font-bold shrink-0">•</span>
                      <span>Referring/exit pages and clickstream data</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 3 */}
            <section id="use" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">3. How We Use Your Data</h2>
              <p className="text-slate-700 leading-relaxed mb-4">
                VMC Reviews uses the collected data for various purposes:
              </p>
              <ul className="space-y-3 ml-4">
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>To provide, maintain, and improve our Service</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>To send you technical notices and support messages</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>To respond to your comments, questions, and requests</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>To monitor and analyze trends, usage, and activities for improving our Service</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>To detect and prevent fraudulent transactions and other illegal activities</span>
                </li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="sharing" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">4. Data Sharing</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                We do not sell, trade, or share your personal information with third parties for marketing purposes. However, we may share data in the following circumstances:
              </p>
              <ul className="space-y-3 ml-4 mb-6">
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Service Providers:</strong> We share data with vendors who help us operate our Service (hosting, analytics, etc.)</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Legal Requirements:</strong> If required by law, court order, or government request</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Business Transfer:</strong> In case of merger, acquisition, or sale of assets</span>
                </li>
              </ul>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-900">
                  <strong>🔓 Your public reviews:</strong> Reviews and ratings you post are publicly visible by design. Business information associated with your profile may also be visible to other users.
                </p>
              </div>
            </section>

            {/* Section 5 */}
            <section id="security" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">5. Data Security</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                We implement comprehensive security measures to protect your personal information:
              </p>
              <ul className="space-y-3 ml-4 mb-6">
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>Passwords are encrypted using bcryptjs (10 rounds)</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>HTTPS encryption for all data in transit</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>Secure authentication with JWT tokens</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span>Regular security audits and updates</span>
                </li>
              </ul>

              <p className="text-slate-700 leading-relaxed">
                While we strive to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </section>

            {/* Section 6 */}
            <section id="retention" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">6. Data Retention</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  We retain your personal data for as long as your account is active or as needed to provide you with our Service. You can request deletion of your account at any time.
                </p>
                <p>
                  After account deletion, we may retain aggregated or anonymized data that cannot identify you. Some data may be retained for legal compliance or to resolve disputes.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section id="rights" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">7. Your Rights</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                You have the following rights regarding your personal data:
              </p>
              <ul className="space-y-3 ml-4 mb-6">
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Access:</strong> Request a copy of your personal data</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Correction:</strong> Request correction of inaccurate data</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Deletion:</strong> Request deletion of your account and associated data</span>
                </li>
                <li className="text-slate-700 flex gap-3">
                  <span className="text-sky-600 font-bold shrink-0">•</span>
                  <span><strong>Opt-out:</strong> Opt out of non-essential communications</span>
                </li>
              </ul>

              <p className="text-slate-700 leading-relaxed">
                To exercise these rights, please contact us at the information provided in the "Contact Us" section below.
              </p>
            </section>

            {/* Section 8 */}
            <section id="cookies" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">8. Cookies and Tracking</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>
              <div>
                <p className="text-slate-700 font-medium mb-4"><strong>Types of cookies we use:</strong></p>
                <ul className="space-y-2 ml-4">
                  <li className="text-slate-700 flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span><strong>Session Cookies:</strong> Temporary cookies for authentication</span>
                  </li>
                  <li className="text-slate-700 flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span><strong>Preference Cookies:</strong> Remember your settings and preferences</span>
                  </li>
                  <li className="text-slate-700 flex gap-3">
                    <span className="text-sky-600 font-bold shrink-0">•</span>
                    <span><strong>Analytics Cookies:</strong> Help us understand how users interact with our Service</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 9 */}
            <section id="contact" className="scroll-mt-8 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">9. Contact Us</h2>
              <p className="text-slate-700 leading-relaxed mb-6">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4 border border-slate-200 mb-6">
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Email</p>
                  <a href="mailto:privacy@vmcreviews.com" className="text-sky-600 font-medium hover:text-sky-700">privacy@vmcreviews.com</a>
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Support</p>
                  <a href="mailto:support@vmcreviews.com" className="text-sky-600 font-medium hover:text-sky-700">support@vmcreviews.com</a>
                </div>
                <div>
                  <p className="text-sm text-slate-600 font-medium mb-1">Address</p>
                  <p className="text-slate-900">VMC Reviews Platform<br />India</p>
                </div>
              </div>

              <p className="text-sm text-slate-600 italic">
                We will respond to your inquiry within 30 days of receipt.
              </p>
            </section>

            {/* CTA Section */}
            <div className="mt-10 pt-10 border-t-2 border-slate-200">
              <div className="rounded-2xl bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 p-8 border border-sky-200 shadow-sm">
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Questions about privacy?</h3>
                    <p className="text-slate-700 leading-relaxed">We're committed to protecting your data. Contact our privacy team with any concerns or questions about how we handle your information.</p>
                  </div>
                  <a
                    href="mailto:privacy@vmcreviews.com"
                    className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 px-8 py-3 text-sm font-bold text-white hover:shadow-lg hover:shadow-sky-200 transition-all duration-200 hover:-translate-y-0.5"
                  >
                    Email Privacy Team
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
